import {
  buildPreferenceDiffPayload,
  syncFeedbackTextToGraph,
  syncPreferenceEventToGraph,
  syncPreferenceExtractionToGraph,
} from "@/lib/learningLedger";
import type { ClientPreferences } from "@/types/preferences";

export type SurfaceFeedbackKind = "positive" | "negative" | "modified";

type ThumbsValue = "up" | "down";

function toThumbs(feedback: SurfaceFeedbackKind): ThumbsValue | null {
  if (feedback === "positive") return "up";
  if (feedback === "negative") return "down";
  return null;
}

async function getIdToken(): Promise<string | null> {
  try {
    const { auth } = await import("@/lib/firebase");
    const currentUser = (auth as { currentUser?: { getIdToken: () => Promise<string> } | null }).currentUser;
    if (!currentUser) return null;
    const token = await currentUser.getIdToken();
    return token || null;
  } catch {
    return null;
  }
}

export async function submitGraphThumbsFeedback(input: {
  feedback: SurfaceFeedbackKind;
  messageId?: string;
  runId?: string;
  memoryIds?: string[];
}): Promise<boolean> {
  const thumbs = toThumbs(input.feedback);
  if (!thumbs) return false;

  const messageId = (input.messageId || input.runId || "").trim();
  const runId = (input.runId || "").trim() || undefined;
  const idToken = await getIdToken();

  const response = await fetch("/api/graph/feedback/thumbs", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    },
    body: JSON.stringify({
      messageId: messageId || undefined,
      runId,
      thumbs,
      memoryIds: Array.isArray(input.memoryIds) ? input.memoryIds : [],
    }),
  });

  return response.ok;
}

export async function submitGraphTextFeedback(input: {
  messageId?: string;
  runId?: string;
  feedbackText: string;
  feedback?: SurfaceFeedbackKind;
  memoryIds?: string[];
}): Promise<FeedbackTextSyncResponse | null> {
  const messageId = (input.messageId || input.runId || "").trim();
  const feedbackText = (input.feedbackText || "").trim();
  if (!messageId || !feedbackText) return null;

  return syncFeedbackTextToGraph({
    messageId,
    runId: input.runId,
    feedbackText,
    thumbs: toThumbs(input.feedback || "modified") || undefined,
    memoryIds: Array.isArray(input.memoryIds) ? input.memoryIds : [],
    idToken: await getIdToken(),
  });
}

export async function submitGraphPreferenceFeedback(input: {
  clientId: string;
  updates: Partial<ClientPreferences>;
  signalType?: "explicit" | "inferred";
  confidence?: number;
  runId?: string;
  messageId?: string;
  source?: string;
}): Promise<boolean> {
  return syncPreferenceEventToGraph(
    {
      userId: "graph-feedback",
      clientId: input.clientId,
      signalType: input.signalType || "explicit",
      updates: input.updates,
      confidence: input.confidence,
      runId: input.runId,
      messageId: input.messageId,
      source: input.source,
    },
    await getIdToken(),
  );
}

export async function submitGraphPreferenceDiffFeedback(input: {
  clientId: string;
  beforeText: string;
  afterText: string;
  runId?: string;
  messageId?: string;
  source?: string;
}): Promise<PreferenceExtractionResponse | null> {
  const payload = buildPreferenceDiffPayload({
    beforeText: input.beforeText,
    afterText: input.afterText,
    runId: input.runId,
    messageId: input.messageId,
    source: input.source || "surface_edit",
  });
  if (!payload) return null;

  return syncPreferenceExtractionToGraph({
    clientId: input.clientId,
    payload,
    idToken: await getIdToken(),
  });
}

export type SurfaceAgentFeedbackResult = {
  candidateId?: string;
  approvalStatus?: FeedbackTextSyncResponse["approvalStatus"] | PreferenceExtractionResponse["approvalStatus"];
  durable?: boolean;
  approved?: boolean;
  sessionOnly?: boolean;
};

function candidateResultFromSync(
  response: FeedbackTextSyncResponse | PreferenceExtractionResponse | null,
): SurfaceAgentFeedbackResult {
  if (!response) return {};
  return {
    ...(response.candidateId ? { candidateId: response.candidateId } : {}),
    ...(response.approvalStatus ? { approvalStatus: response.approvalStatus } : {}),
    ...(typeof response.durable === 'boolean' ? { durable: response.durable } : {}),
    ...(typeof response.approved === 'boolean' ? { approved: response.approved } : {}),
    ...(typeof response.sessionOnly === 'boolean' ? { sessionOnly: response.sessionOnly } : {}),
  };
}

export async function submitSurfaceAgentFeedback(input: {
  feedback: SurfaceFeedbackKind;
  messageId?: string;
  runId?: string;
  feedbackText?: string;
  memoryIds?: string[];
  clientId?: string;
  beforeText?: string;
  afterText?: string;
  source?: string;
}): Promise<SurfaceAgentFeedbackResult> {
  let result: SurfaceAgentFeedbackResult = {};
  await submitGraphThumbsFeedback({
    feedback: input.feedback,
    messageId: input.messageId,
    runId: input.runId,
    memoryIds: input.memoryIds,
  });

  if (input.feedbackText) {
    const textResult = await submitGraphTextFeedback({
      messageId: input.messageId,
      runId: input.runId,
      feedbackText: input.feedbackText,
      feedback: input.feedback,
      memoryIds: input.memoryIds,
    });
    result = {
      ...result,
      ...candidateResultFromSync(textResult),
    };
  }

  if (input.clientId && input.beforeText !== undefined && input.afterText !== undefined) {
    const diffResult = await submitGraphPreferenceDiffFeedback({
      clientId: input.clientId,
      beforeText: input.beforeText,
      afterText: input.afterText,
      runId: input.runId,
      messageId: input.messageId,
      source: input.source,
    });
    result = {
      ...result,
      ...candidateResultFromSync(diffResult),
    };
  }
  return result;
}
